import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import './Buttons.css';
import './ShoppingCart.css';

export default function ShoppingCart({ currentGardener, userPlantData, currentDate, onClose }) {
    // Delivery / Pickup flow
    const [showDeliveryChoice, setShowDeliveryChoice] = useState(false);
    const [needsAddress, setNeedsAddress] = useState(false);
    const [orderSummary, setOrderSummary] = useState(null);
    const [showApproval, setShowApproval] = useState(false);
    const [showEmailFixModal, setShowEmailFixModal] = useState(false);
    const [updatedEmail, setUpdatedEmail] = useState("");


    const [deliveryAddress, setDeliveryAddress] = useState({
        street: "",
        city: "",
        postal: ""
    });

    const [tasks, setTasks] = useState([]);
    const [starters, setStarters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [supplierChoices, setSupplierChoices] = useState([]);
    const [needsSupplierChoice, setNeedsSupplierChoice] = useState(false);
    const [selectedCrop, setSelectedCrop] = useState(null);
    const [packetSizes, setPacketSizes] = useState([]);
    const [orderComplete, setOrderComplete] = useState(false);

    // ------------------------------------------------------------
    //  Supplier Matching
    // ------------------------------------------------------------
    const [supplier, setSupplier] = useState(null);

    useEffect(() => {
        if (!supplier?.id) return;

        const fetchAvailableCrops = async () => {
            try {
                const crops = await api.get(`/supplierPlantData/activeCrops/${supplier.id}`);

                setSupplier(prev => ({
                    ...prev,
                    availableCrops: crops.data || []
                }));
            } catch (err) {
                console.error("Error fetching available crops:", err);
            }
        };

        fetchAvailableCrops();
    }, [supplier?.id]);


    useEffect(() => {
        if (!supplier?.id) return;

        const fetchPacketOptions = async () => {
            const res = await api.get(`/supplierPrice/bySupplier/${supplier.id}`);

            setPacketSizes(
                res.data.map(p => ({
                    packetSizeID: p.id,
                    supplierID: p.supplierID,
                    packetSize: Number(p.packetSize),
                    packetLabel:  p.description, 
                    price: Number(p.price)
                }))
            );
        };

        fetchPacketOptions();
    }, [supplier?.id]);

    useEffect(() => {
        if (!supplier?.id || !currentGardener?.locationID) return;

        const fetchDeliveryCost = async () => {
            try {
                const res = await api.get(
                    `/supplierPrice/byCatchment/${supplier.id}/${currentGardener.locationID}`
                );

                // API returns: { deliveryCost: number | null }
                setSupplier(prev => ({
                    ...prev,
                    deliveryCost: res.data.deliveryCost   // null means no delivery
                }));
            } catch (err) {
                console.error("Error fetching delivery cost:", err);
            }
        };

        fetchDeliveryCost();
    }, [supplier?.id, currentGardener?.locationID]);


useEffect(() => {
    if (!currentGardener) return;

    const fetchSupplier = async () => {
        try {
            let chosenSupplierID = null;

            // If gardener already has a preferred supplier
            if (currentGardener.supplierID) {
                chosenSupplierID = currentGardener.supplierID;
            } else {
                // No preferred supplier then match by city
                const matches = await api.get(`/supplier/ByCatchment/${currentGardener.locationID}`);
                
                const supplierList = matches.data; //array of {supplierID, name}
                
                if (supplierList.length === 0) {
                    console.warn("No suppliers found for this catchment"); 
                    return; 
                }

                if (supplierList.length === 1) {
                    // Auto-select 
                    chosenSupplierID = supplierList[0].supplierID;                
                } else if (supplierList.length > 1) {
                    // MULTIPLE SUPPLIERS so gardener must choose 
                    setSupplierChoices(supplierList); 
                    setNeedsSupplierChoice(true); 
                    return; // stop here until user chooses                
                }
            }
            const sup = await api.get(`/supplier/${chosenSupplierID}`);

            setSupplier({
                ...sup.data,
                availableCrops: []   
            });

        } catch (err) {
            console.error("Error fetching supplier:", err);
        }
    };

    fetchSupplier();
}, [currentGardener]);

const startersNeedingVarieties = starters.filter(s => s.plantID); 

useEffect(() => {
    if (!supplier) return;
    if (packetSizes.length === 0) return;

    const loadAllSupplierVarieties = async () => {


        const updated = await Promise.all(
            starters.map(async starter => {

                if (starter.supplierVariety || starter.totalCount > 0 || starter.isAdditional) {
                    return starter;
                }

                if (!starter.plantID) { 
                    return starter;
                }

                const response = await api.get('/supplierPlantData', {
                    params: {
                        supplierID: supplier.id,
                        plantID: starter.plantID
                    }
                });

                // Only available varieties
                const available = response.data.filter(v =>
                    v.AvailableFlag === 1 || v.AvailableFlag === true
                );

                // Try to auto-match by name
                const match = available.find(v =>
                    v.variety.toLowerCase() === starter.variety.toLowerCase()
                );

                // Look up packet size info using packetSizeID 
                const packetMeta = match
                    ? packetSizes.find(ps => ps.packetSizeID === match.PacketSizeID)
                    : null;

                console.log("packetSizes:", packetSizes);
                console.log("match:", match);
                console.log("packetMeta:", packetMeta);

                const PacketSize = packetMeta ? packetMeta.packetSize : null;
                const PacketLabel = packetMeta ? packetMeta.packetLabel : "?";
                const PacketPrice = packetMeta ? packetMeta.price : null;

                // fetch plant metadata
                const plant = await api.get(`/plants/${starter.plantID}`);
                const plantData = plant.data;

                // GUARD: if plantData is missing, skip math 
                if (!plantData || !plantData.Amount ) { 
                    return {
                        ...starter,
                        plantData,                 
                        supplierVarieties: available, 
                        supplierVariety: match ? match.variety : "",
                        PacketSize,
                        PacketLabel,
                        PacketPrice,
                        totalPlants: 0,
                        totalCount: 0
                    };
                }

                //compute total plants needed
                const totalPlants = computeTotalPlants(plantData, currentGardener);
                const packs = match 
                    ? computePacksNeeded(totalPlants, PacketSize) 
                    : 0;

                return {
                        ...starter,
                        plantData,                 
                        supplierVarieties: available, 
                        supplierVariety: match ? match.variety : "",
                        PacketSize: PacketSize,
                        PacketLabel,
                        PacketPrice,
                        totalPlants,
                        totalCount: packs
                };
            })
        );

        setStarters(updated);
    };

    loadAllSupplierVarieties();
}, [supplier, packetSizes]);

    // ------------------------------------------------------------
    // 1. Compute NEXT MONTH window
    // ------------------------------------------------------------
    const nextMonthWindow = useMemo(() => {
        const now = new Date(currentDate);
        const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);

        const start = new Date(next.getFullYear(), next.getMonth(), 1);
        const end = new Date(next.getFullYear(), next.getMonth() + 1, 0);

        return {
            label: start.toLocaleString('default', { month: 'long' }),
            start,
            end
        };
    }, [currentDate]);

    // ------------------------------------------------------------
    // 2. Fetch scheduleTask items for this gardener
    // ------------------------------------------------------------
    useEffect(() => {
        if (!currentGardener?.id) return;

        const fetchTasks = async () => {
            try {
                const response = await api.get(`/tasks/threeMonthTasks/${currentGardener?.id}`, {
                    params: { scheduleTable: currentGardener?.scheduleTable }
                });
                if (response.data) {
                    setTasks(response.data);
                    setLoading(false);
                }

            } catch (err) { 
                console.error("Error fetching tasks:", err); 
            }
        };

        fetchTasks();
    }, [currentGardener?.id]);

    // ------------------------------------------------------------
    // 3. Filter DS + TP tasks that fall inside NEXT MONTH
    // ------------------------------------------------------------
useEffect(() => {
    if (!tasks) return;

    const filtered = tasks.filter(task => {
        const taskDate = new Date(task.date);
        taskDate.setMinutes(taskDate.getMinutes() + taskDate.getTimezoneOffset());
        taskDate.setHours(0, 0, 0, 0);

    const isDirectSow = task.plantingMethod === "Direct sow";
    const isNextMonth =
        taskDate >= nextMonthWindow.start &&
        taskDate <= nextMonthWindow.end;

    return isDirectSow && isNextMonth;
});

setStarters(prev => prev.length === 0 ? filtered : prev);
}, [tasks]);

useEffect(() => {
if (!selectedCrop || !supplier || !selectedCrop.plantID) return;

    const fetchVarieties = async () => {
        const response = await api.get('/supplierPlantData', {
            params: {
                supplierID: supplier.id,
                plantID: selectedCrop.plantID
            } 
         });

        const available = response.data.filter(v => 
            v.AvailableFlag === true || v.AvailableFlag === 1 
        );
        
        setStarters(prev =>
            prev.map(s =>
                s.id === selectedCrop.id
                    ? {
                        ...s,
                        supplierVarieties: available
                    }
                    : s
            )
        );

        // Auto-match supplier variety if names match
        const match = response.data.find(v =>
            v.AvailableFlag === 1 &&
            v.variety.toLowerCase() === selectedCrop.variety.toLowerCase()
        );

        if (match) {
            setStarters(prev =>
                prev.map(s =>
                    s.id === selectedCrop.id
                        ? { ...s, supplierVariety: match.variety }
                        : s
                )
            );
        }


    };

    fetchVarieties();
}, [selectedCrop, supplier]);

    // ------------------------------------------------------------
    // 4. Render
    // ------------------------------------------------------------
    if (loading) {
        return <div>Loading your starters...</div>;
    }

function parseThinout(thinout) {
    if (!thinout) return 4; // fallback spacing

    const parts = thinout.split('-').map(Number);
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return (parts[0] + parts[1]) / 2;

    return 4; // fallback
}


function computeTotalPlants(plantData, gardener) {
    const amt = Number(plantData.Amount) || 1;
    const type = plantData.AmountType?.toLowerCase();
    const family = Number(gardener.familysize) || 1;

    // 1. Per plant / bulbs / crowns 
    if (["plant", "plants", "bulbs", "crowns"].includes(type)) { 
        return amt * family; 
    }

    // 2. Feet-based crops 
    if (type === "feet") { 
        const spacing = parseThinout(plantData.Thinout); // e.g. "3-4" 
        const plantsPerFoot = 12 / spacing; 
        return Math.ceil(amt * plantsPerFoot * family); 
    }
}

function computePacksNeeded(totalPlants, packetSize) {
    if (!packetSize || packetSize <= 0) return 0;
    return Math.ceil(totalPlants / packetSize);
}

function getVarietiesForPlant(starter) {
    return starter.supplierVarieties || [];
}

    function handleVarietySelect(starter, varietyName) {

        const selectedVariety = starter.supplierVarieties
            ?.find(v => v.variety === varietyName);

        console.log("selectedVariety:", selectedVariety);

        const packetMeta = packetSizes.find(
            ps => ps.packetSizeID === selectedVariety?.PacketSizeID
        );

        let totalPlants = 0;
        let packs = starter.totalCount;

        if (starter.plantData && !starter.isAdditional) { 
            totalPlants = computeTotalPlants(
                starter.plantData,
                currentGardener
            );

            packs = packetMeta
                ? computePacksNeeded(totalPlants, packetMeta.packetSize)
                : 0;
        }

        setStarters(prev =>
            prev.map(s =>
                s.id === starter.id
                    ? {
                        ...s,
                        supplierVariety: varietyName,

                        PacketSize: packetMeta?.packetSize,
                        PacketSizeID: packetMeta?.packetSizeID,
                        PacketLabel: packetMeta?.packetLabel,
                        Price: packetMeta?.price,

                        totalPlants,
                        totalCount: packs
                    }
                    : s
            )
        );
    }
function addNewStarterRow() {
    const newRow = {
        id: crypto.randomUUID(),
        crop: "",
        variety: "",
        plantID: null,
        plantData: null,
        supplierVarieties: [],
        supplierVariety: "",
        selectedVarieties: [],
        PacketSize: null,
        totalPlants: 0,
        totalCount: 0,
        isNew: true,
        isAdditional: true
    };

    setStarters(prev => [...prev, newRow]);
}

const handleCropSelect = (row, selectedName) => {
    // Find the crop object by NAME
    const crop = supplier.availableCrops.find(c => c.name === selectedName);

    if (!crop) {
        console.error("Crop not found:", selectedName);
        return;
    }

    const updated = starters.map(s =>
        s.id === row.id
            ? {
                ...s,
                crop: selectedName,     // store the name
                plantID: crop.plantID,  // store the plantID
                variety: "",
                supplierVariety: "",
                supplierVarieties: [],
                PacketSize: null,
                totalPlants: 0,
                totalCount: 0,
                isNew: false            // row is now �hydrated�
            }
            : s
    );

    setStarters(updated);
};

    function updateTotalCount(id, newValue) {
        setStarters(prev =>
            prev.map(row =>
                row.id === id
                    ? { ...row, totalCount: parseInt(newValue) || 0 }
                    : row
            )
        );
    }
    function handleCompleteOrder() {
        // remove the add additional rows and complete order buttons
        setOrderComplete(true);

        const hasDelivery = supplier?.deliveryCost != null;
        const hasPickup = true; // until you add offersPickup flag

        // CASE A: Pickup only
        if (hasPickup && !hasDelivery) {
            prepareOrder("pickup");
            return;
        }

        // CASE B: Delivery only (future seed mail-out suppliers)
        if (!hasPickup && hasDelivery) {
            setNeedsAddress(true);
            return;
        }

        // CASE C: Both options therefore show choice UI
        setShowDeliveryChoice(true);
    }

    function prepareOrder(method) {
        setShowDeliveryChoice(false);

        // Filter valid rows only
        const validItems = starters
            .filter(s =>
                s.plantID &&
                s.supplierVariety &&
                s.totalCount > 0
            )
            .map(s => {

                const selectedSupplierPlant = s.supplierVarieties
                    ?.find(v => v.variety === s.supplierVariety);

                return {
                    plantID: s.plantID,
                    supplierPlantID: selectedSupplierPlant?.supplierPlantID,
                    variety: s.variety || null,
                    supplierVariety: s.supplierVariety,
                    totalPackets: s.totalCount,
                    price: Number(s.Price ?? s.PacketPrice ?? 0)
                };
            });

        if (validItems.length === 0) {
            return; // Or show inline message in UI
        }

        const subtotal = validItems.reduce((sum, item) => sum + item.totalPackets * item.price, 0);
        const deliveryFee = method === "delivery"
            ? Number(supplier.deliveryCost ?? 0)
            : 0;

        const GSTTaxRate = 0.05;
        const gst = (subtotal + deliveryFee) * GSTTaxRate;

        const total = subtotal + deliveryFee + gst;

        setOrderSummary({
            method,
            items: validItems,
            gst,
            subtotal,
            deliveryFee,
            GSTTaxRate,
            total
        });

        setShowApproval(true);
    }

    async function confirmOrder() {


        if (!orderSummary) return;

        const payload = {
            gardenerID: currentGardener.id,
            supplierID: supplier.id,
            month: nextMonthWindow.label,
            method: orderSummary.method,
            deliveryAddress: orderSummary.method === "delivery" ? deliveryAddress : null,
            subtotal: orderSummary.subtotal,
            gst: orderSummary.gst,
            deliveryFee: orderSummary.deliveryFee,
            total: orderSummary.total,
            GSTTaxRate: orderSummary.GSTTaxRate,
            items: orderSummary.items
        };

        try {
            await api.post("/gardenerOrders", payload);
            onClose(); // close modal / drawer
            setShowApproval(false);
            setOrderSummary(null);
        } catch (err) {
            console.error("Order submission failed:", err.response?.data || err);
        }
    }

    if (needsSupplierChoice) {
        return (
            <div className="supplier-choice-container">
                <div className="supplier-choice-card">
                    <h2>Select Your Supplier</h2>
                    <p className="supplier-choice-subtext">
                        Choose where your starters will be grown and prepared this month.
                    </p>

                    <div className="supplier-list">
                        {supplierChoices.map(s => (
                            <div
                                key={s.supplierID}
                                className="supplier-card"
                                onClick={async () => {
                                    const sup = await api.get(`/supplier/${s.supplierID}`);
                                    setSupplier(sup.data);
                                    setNeedsSupplierChoice(false);
                                }}
                            >
                                <div className="supplier-card-logo">
                                    {s.logoUrl && <img src={s.logoUrl} alt={s.name} />}
                                </div>

                                <h3 className="supplier-card-name">{s.name}</h3>

                                {s.website && (
                                    <a
                                        href={s.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="supplier-card-website"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {new URL(s.website).hostname.replace("www.", "")}
                                    </a>
                                )}

                                <div className="supplier-card-address">
                                    <p>{s.StreetAddress}</p>
                                    <p>{s.city}, {s.province} {s.PostalCode}</p>
                                    <p>{s.Country}</p>
                                </div>

                                <div className="supplier-badges">
                                    {s.offersPickup && <span className="badge pickup">Pickup</span>}
                                    {s.deliveryCost !== null && (
                                        <span className="badge delivery">
                                            Delivery {s.deliveryCost > 0 ? `$${s.deliveryCost}` : "Free"}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    {showEmailFixModal && (
    <div className="modal-overlay">
        <div className="modal">
            <h2>Update Your Email</h2>
            <p>Your email address appears to be invalid. Please update it before placing your order.</p>

            <input
                type="email"
                value={updatedEmail}
                onChange={(e) => setUpdatedEmail(e.target.value)}
                placeholder="Enter a valid email"
                className="email-input"
            />

            <div className="modal-buttons">
                <button
                    className="save-button"
                    onClick={async () => {
                        try {
                            await api.post("/gardeners/updateEmail", { email: updatedEmail });
                            setShowEmailFixModal(false);
                        } catch (err) {
                            console.error("Failed to update email:", err);
                        }
                    }}
                >
                    Save Email
                </button>

                <button
                    className="cancel-button"
                    onClick={() => setShowEmailFixModal(false)}
                >
                    Cancel
                </button>
            </div>
        </div>
    </div>
)}


    return (
    <div className="shopping-cart">

        {/* Close Button */}
        <button className="close-button" onClick={onClose}>
            Close
        </button>

            {supplier && (
            <div className="supplier-header">
                {supplier.logoURL && (
                <img
                    src={supplier.logoURL}
                    alt={`${supplier.name} logo`}
                    className="supplier-logo"
                />
                )}

                {/* Center Header */}
                <div className="supplier-title">
                    <h1>Your Starters for {nextMonthWindow.label}</h1>
                </div>

                <div className="supplier-info">
                <h2>{supplier.name}</h2>

                <p>{supplier.streetAddress}</p>
                <p>{supplier.city}, {supplier.province} {supplier.postalCode}</p>
                <p>{supplier.country}</p>

                {supplier.website && (
                    <a href={supplier.website} target="_blank" rel="noreferrer">
                    {supplier.website}
                    </a>
                )}
                </div>
            </div>
            )}

<div className="cart-content">
            <div className="starter-table">
              <table className="clean-table">
                <thead>
                        <tr>
                            <th>Crop</th>
                            <th>Your Variety</th>
                            <th>Supplier Variety</th>
                            <th>Total Packets</th>
                    </tr>
                </thead>
                    <tbody>

                        {/* ------------------------------ */}
                        {/* 1. Recommended From Your Plan */}
                        {/* ------------------------------ */}
                        <tr>
                            <td colSpan="4" className="section-header">
                                Recommended for You (based on your garden plan)
                            </td>
                        </tr>

                        {starters
                            .filter(row => !row.isAdditional)
                            .map(row => (
                                <tr
                                    key={row.id}
                                    onClick={!orderComplete ? () => setSelectedCrop(row) : undefined}
                                    className={selectedCrop?.id === row.id ? "selected" : ""}
                                >
                                    <td>{row.crop}</td>
                                    <td>{row.variety || row.supplierVariety}</td>
                                    <td>
                                        {(!row.supplierVarieties || row.supplierVarieties.length === 0) ? (
                                            <span className="no-availability">
                                                No starters available at this time.
                                            </span>
                                        ) : (
                                        selectedCrop?.id === row.id ? (
                                            <select
                                                disable={orderComplete}
                                                value={row.supplierVariety || ""}
                                                onChange={(e) => handleVarietySelect(row, e.target.value)}
                                            >
                                                <option value="">Choose...</option>
                                                {row.supplierVarieties.map(v => (
                                                    <option key={v.supplierPlantID} value={v.variety}>
                                                        {v.variety} (
                                                            {packetSizes.find(ps => ps.packetSizeID === v.PacketSizeID)?.packetLabel || "?"}
                                                        )
                                                     </option>
                                                ))}
                                            </select>
                                        ) : (
                                                <span>{row.supplierVariety || "Choose..."}
                                                    {row.PacketLabel ? `(${row.PacketLabel})` : ""}
                                                            </span>
                                        )
                                        )}
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            min="0"
                                            disabled={orderComplete || !row.supplierVarieties?.length}
                                            value={row.totalCount}
                                            onChange={(e) => updateTotalCount(row.id, e.target.value)}
                                            style={{ width: "70px" }}
                                        />
                                    </td>
                                </tr>
                            ))}

                        {/* ------------------------------ */}
                        {/* 2. Add More Starters (optional) */}
                        {/* ------------------------------ */}
                        <tr>
                            <td colSpan="4" className="section-header">
                                Add More Starters (optional)
                            </td>
                        </tr>

                        {starters
                            .filter(row => row.isAdditional)
                            .map(row => (
                                <tr
                                    key={row.id}
                                    onClick={!orderComplete ? () => setSelectedCrop(row) : undefined}
                                    className={selectedCrop?.id === row.id ? "selected" : ""}
                                >
                                    <td>
                                        <select
                                            disabled={orderComplete}
                                            value={row.crop}
                                            onChange={(e) => handleCropSelect(row, e.target.value)}
                                        >
                                            <option value="">Choose crop...</option>
                                            {(supplier?.availableCrops || []).map(c => (
                                                <option key={c.plantID} value={c.name}>
                                                    {c.name}
                                                </option>
                                            ))}
                                        </select>
                                    </td>

                                    <td>{row.variety || ""}</td>

                                    <td>
                                        {selectedCrop?.id === row.id ? (
                                            <select
                                                disabled={orderComplete}
                                                value={row.supplierVariety || ""}
                                                onChange={(e) => handleVarietySelect(row, e.target.value)}
                                            >
                                                <option value="">Choose...</option>
                                                {(row.supplierVarieties || []).map(v => {
                                                    const meta = packetSizes.find(ps => ps.packetSizeID === v.PacketSizeID);
                                                    return (
                                                        <option key={v.supplierPlantID} value={v.variety}>
                                                            {v.variety} ({meta ? meta.packetLabel : "?"})
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        ) : (
                                                <span>{row.supplierVariety || "Choose..."}
                                                    {row.PacketLabel ? `(${row.PacketLabel})` : ""}
                                                </span>
                                        )}
                                    </td>

                                    <td>
                                        <input
                                            type="number"
                                            min="0"
                                            disabled={orderComplete}
                                            value={row.totalCount}
                                            onChange={(e) => updateTotalCount(row.id, e.target.value)}
                                            style={{ width: "70px" }}
                                        />
                                    </td>
                                </tr>
                            ))}

                    </tbody>
                </table>
                
                {!orderComplete && (
                    <>
                        <button className="button" onClick={addNewStarterRow}>
                            Add another crop or variety
                        </button>

                        <button
                            className="button complete-order-button"
                            onClick={handleCompleteOrder}
                        >
                            Complete Order
                        </button>
                    </>
                )}

                {showDeliveryChoice && supplier?.deliveryCost != null && (
                    <div className="delivery-choice-box">
                        <h3>How would you like to receive your starters?</h3>

                        <button className="button" onClick={() => prepareOrder("pickup")}>
                            Pickup at {supplier.name}
                        </button>

                            {/* Delivery only if deliveryCost is NOT null */}
                            {supplier?.deliveryCost != null && (
                                <button className="button" onClick={() => setNeedsAddress(true)}>
                                    Delivery {supplier.deliveryCost > 0
                                        ? `($${supplier.deliveryCost})`
                                        : "(Free)"}
                                </button>
                            )}
                    </div>
                )}

                {needsAddress && (
                    <div className="address-box">
                        <h3>Delivery Address</h3>

                        <input
                            type="text"
                            placeholder="Street Address"
                            value={deliveryAddress.street}
                            onChange={(e) => setDeliveryAddress({ ...deliveryAddress, street: e.target.value })}
                        />

                        <input
                            type="text"
                            placeholder="City"
                            value={deliveryAddress.city}
                            onChange={(e) => setDeliveryAddress({ ...deliveryAddress, city: e.target.value })}
                        />

                        <input
                            type="text"
                            placeholder="Postal Code"
                            value={deliveryAddress.postal}
                            onChange={(e) => setDeliveryAddress({ ...deliveryAddress, postal: e.target.value })}
                        />

                        <button className="button" onClick={() => prepareOrder("delivery")}>
                            Confirm Delivery Order
                        </button>
                    </div>
                )}

                {showApproval && orderSummary && (
                    <div className="order-approval">
                    <h3>
                        {orderSummary.method === "pickup"
                            ? `Pickup at: ${supplier.name}`
                            : `Delivered to: ${deliveryAddress}`}
                        </h3>

                        <h3>Confirm Your Order</h3>

                        <ul>
                            {orderSummary.items.map((item, i) => (
                                <li key={i}>
                                    <strong>{item.supplierVariety}</strong> - {item.totalPackets} x ${item.price.toFixed(2)}
                                    = ${(item.totalPackets * item.price).toFixed(2)}                                </li>
                            ))}
                        </ul>

                        <p>Subtotal: ${orderSummary.subtotal.toFixed(2)}</p>
                        {orderSummary.deliveryFee > 0 && <p>Delivery: ${orderSummary.deliveryFee.toFixed(2)}</p>}

                            <p>GST: ${orderSummary.gst.toFixed(2)}</p>
                            <p><strong>Total: ${orderSummary.total.toFixed(2)}</strong></p>

                            <p>
                                <strong>You are preordering next month's starters.</strong><br />
                                This amount will be charged on the <strong>last day of the month</strong> using your subscription payment method.
                            </p>

                            <p>
                                Suppliers make every effort to provide the exact variety you selected.
                                If a crop fails before pickup, a similar substitution will be offered.
                                Starter plants are non-returnable.
                            </p>

                        <button className="button" onClick={confirmOrder}>Approve & Add to Subscription</button>
                        <button className="button" onClick={() => setShowApproval(false)}>Cancel</button>
                    </div>
                )}

            </div>

         </div>
       </div>
    );
}